/**
 * Brasil Transparente — MVP Pernambuco (PE).
 *
 * Rota: /brasil-transparente
 * Mobile-first. Tabs: Overview, Representantes, Municípios, Emendas, Salários.
 *
 * Dados 100% reais consumidos do backend Public Data Hub. Nenhum mock.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  MapPin, Users, Building2, Coins, Wallet, Search,
  AlertCircle, CheckCircle2, XCircle, ExternalLink, Info,
  Briefcase, Receipt, Share2, ChevronRight, Landmark, Star,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const brl = (v) =>
  typeof v === 'number'
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const num = (v) =>
  typeof v === 'number' ? v.toLocaleString('pt-BR') : '—';

// UFs no seletor (só PE ativo no MVP)
const UFS = [
  { code: 'PE', nome: 'Pernambuco', ativa: true },
  { code: 'BA', nome: 'Bahia', ativa: false },
  { code: 'CE', nome: 'Ceará', ativa: false },
  { code: 'RJ', nome: 'Rio de Janeiro', ativa: false },
  { code: 'SP', nome: 'São Paulo', ativa: false },
];

const TABS = [
  { key: 'overview', label: 'Visão geral', icon: MapPin },
  { key: 'representantes', label: 'Representantes', icon: Users },
  { key: 'estaduais', label: 'Deputados estaduais', icon: Landmark },
  { key: 'municipios', label: 'Municípios', icon: Building2 },
  { key: 'emendas', label: 'Emendas', icon: Coins },
  { key: 'gabinete', label: 'Gastos de gabinete', icon: Briefcase },
  { key: 'empresas', label: 'Empresas beneficiadas', icon: Receipt },
  { key: 'salarios', label: 'Salários', icon: Wallet },
];

const YEARS = [2026, 2025, 2024, 2023, 2022];
const YEAR_ELEITORAL = 2026;

export default function BrasilTransparente() {
  const [uf] = useState('PE'); // MVP: fixed
  const [tab, setTab] = useState('overview');

  return (
    <div className="min-h-screen bg-neutral-950 text-white pb-24">
      {/* Header */}
      <header
        className="border-b border-neutral-800 bg-gradient-to-br from-emerald-900/40 via-neutral-950 to-neutral-950 px-4 py-6 sm:py-10"
        data-testid="bt-header"
      >
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[11px] uppercase tracking-widest text-emerald-300/80 font-mono">
              MVP • dados oficiais
            </span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-none">
            Brasil <span className="text-emerald-400">Transparente</span>
          </h1>
          <p className="text-neutral-400 mt-3 text-sm sm:text-base max-w-2xl">
            Um só lugar para acompanhar quem representa você, para onde vai o
            dinheiro público e quais são os salários oficiais no seu estado.
            Começamos por <strong className="text-white">Pernambuco</strong>.
          </p>

          {/* Seletor UF */}
          <div className="mt-5 flex gap-2 overflow-x-auto -mx-4 px-4 pb-1">
            {UFS.map((u) => (
              <button
                key={u.code}
                disabled={!u.ativa}
                data-testid={`uf-btn-${u.code}`}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  u.ativa && u.code === uf
                    ? 'bg-emerald-500 text-neutral-950 border-emerald-500'
                    : u.ativa
                    ? 'bg-neutral-900 text-neutral-200 border-neutral-700 hover:border-emerald-500'
                    : 'bg-neutral-900/40 text-neutral-600 border-neutral-800 cursor-not-allowed line-through'
                }`}
              >
                {u.code}
                {!u.ativa && <span className="ml-1 opacity-60">em breve</span>}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Tabs */}
      <nav className="sticky top-0 z-10 bg-neutral-950/95 backdrop-blur border-b border-neutral-800">
        <div className="max-w-5xl mx-auto flex overflow-x-auto scrollbar-none">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`bt-tab-${t.key}`}
                className={`shrink-0 flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
                  active
                    ? 'border-emerald-500 text-white'
                    : 'border-transparent text-neutral-500 hover:text-neutral-200'
                }`}
              >
                <Icon size={16} />
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        {tab === 'overview' && <OverviewTab uf={uf} />}
        {tab === 'representantes' && <RepresentantesTab uf={uf} />}
        {tab === 'estaduais' && <EstaduaisTab uf={uf} />}
        {tab === 'municipios' && <MunicipiosTab uf={uf} />}
        {tab === 'emendas' && <EmendasTab uf={uf} />}
        {tab === 'gabinete' && <GabineteTab uf={uf} />}
        {tab === 'empresas' && <EmpresasTab uf={uf} />}
        {tab === 'salarios' && <SalariosTab uf={uf} />}
      </main>
    </div>
  );
}

/* ─── Overview ──────────────────────────────────────────────── */
function OverviewTab({ uf }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErr(false);
      try {
        const r = await axios.get(`${API}/public/brasil-transparente/${uf}/overview`, { timeout: 30000 });
        if (!cancel) setData(r.data);
      } catch { if (!cancel) setErr(true); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [uf]);

  if (loading) return <SkeletonBlock />;
  if (err || !data?.ok) return <ErrorBox />;

  return (
    <div className="space-y-4" data-testid="bt-overview">
      {/* Banner ano eleitoral 2026 */}
      <Card
        className="bg-gradient-to-r from-amber-500/15 to-amber-500/5 border-amber-500/40 p-4 flex items-start gap-3"
        data-testid="banner-2026"
      >
        <Star size={18} className="text-amber-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-amber-300 font-mono">
            Ano eleitoral · 2026
          </div>
          <div className="text-sm text-neutral-200 mt-1">
            Emendas, contratos e gastos públicos ganham atenção redobrada neste ano.
            Use o filtro <b>2026</b> nas abas Emendas e Municípios para focar.
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard
          testid="metric-deputados"
          label="Deputados federais"
          value={num(data.representantes?.deputados_federais)}
          hint="Câmara"
        />
        <MetricCard
          testid="metric-senadores"
          label="Senadores"
          value={num(data.representantes?.senadores)}
          hint="Senado"
        />
        <MetricCard
          testid="metric-municipios"
          label="Municípios"
          value={num(data.municipios?.total)}
          hint="IBGE"
        />
        <MetricCard
          testid="metric-emendas"
          label="Emendas p/ PE"
          value={brl(data.emendas?.total_reais)}
          hint={`${num(data.emendas?.count)} pagamentos`}
        />
      </div>

      {/* Status providers */}
      <Card className="bg-neutral-900 border-neutral-800 p-4">
        <h3 className="text-sm font-semibold text-neutral-300 mb-3 flex items-center gap-2">
          <Info size={14} /> Saúde das fontes
        </h3>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {Object.entries(data.providers_status || {}).map(([code, ok]) => (
            <div
              key={code}
              className="flex items-center gap-2 px-2 py-1.5 rounded bg-neutral-950 border border-neutral-800"
              data-testid={`provider-status-${code}`}
            >
              {ok ? (
                <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
              ) : (
                <XCircle size={14} className="text-red-400 shrink-0" />
              )}
              <span className="font-mono text-neutral-300 truncate">{code}</span>
            </div>
          ))}
        </div>
      </Card>

      <FontesFooter uf={uf} />
    </div>
  );
}

/* ─── Representantes ───────────────────────────────────────── */
function RepresentantesTab({ uf }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErr(false);
      try {
        const r = await axios.get(`${API}/public/brasil-transparente/${uf}/representantes`, { timeout: 30000 });
        if (!cancel) setData(r.data);
      } catch { if (!cancel) setErr(true); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [uf]);

  if (loading) return <SkeletonBlock />;
  if (err) return <ErrorBox />;

  const dep = data?.deputados_federais || [];
  const sen = data?.senadores || [];

  return (
    <div className="space-y-6" data-testid="bt-representantes">
      <Section title="Senadores" count={sen.length} testid="section-senadores">
        {sen.length === 0 ? (
          <EmptyLine msg="Serviço do Senado indisponível no momento." />
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {sen.map((s) => <PersonCard key={s.id} p={s} />)}
          </div>
        )}
      </Section>

      <Section title="Deputados federais" count={dep.length} testid="section-deputados">
        {dep.length === 0 ? (
          <EmptyLine msg="Serviço da Câmara indisponível no momento." />
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {dep.map((d) => <PersonCard key={d.id} p={d} />)}
          </div>
        )}
      </Section>
    </div>
  );
}

function PersonCard({ p }) {
  const isSenator = p.cargo && p.cargo.toLowerCase().includes('senador');
  return (
    <Card
      className="bg-neutral-900 border-neutral-800 p-3 flex items-center gap-3"
      data-testid={`person-card-${p.id}`}
    >
      <img
        src={p.foto || 'https://via.placeholder.com/80x100?text=%20'}
        alt={p.nome}
        loading="lazy"
        className="w-14 h-16 object-cover rounded bg-neutral-800 shrink-0"
        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
      />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-white truncate">{p.nome}</div>
        <div className="text-xs text-neutral-400 mt-0.5">
          {p.cargo} · <span className="text-emerald-400">{p.partido}</span> · {p.uf}
        </div>
        {p.email && (
          <a
            href={`mailto:${p.email}`}
            className="text-[11px] text-neutral-500 hover:text-emerald-400 truncate block mt-1"
          >
            {p.email}
          </a>
        )}
      </div>
      {!isSenator && (
        <Link
          to={`/brasil-transparente/deputado/${p.id}`}
          data-testid={`open-dep-${p.id}`}
          className="text-[11px] px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 shrink-0"
        >
          Perfil
        </Link>
      )}
      {isSenator && (
        <a
          href={`https://www25.senado.leg.br/web/senadores/senador/-/perfil/${p.id}`}
          target="_blank"
          rel="noreferrer"
          data-testid={`open-sen-${p.id}`}
          className="text-[11px] px-2 py-1 rounded bg-neutral-800 border border-neutral-700 text-neutral-300 hover:border-emerald-500 shrink-0 inline-flex items-center gap-1"
        >
          <ExternalLink size={11} /> Senado
        </a>
      )}
    </Card>
  );
}

/* ─── Municípios ──────────────────────────────────────────── */
function MunicipiosTab({ uf }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/public/brasil-transparente/${uf}/municipios${query ? `?q=${encodeURIComponent(query)}` : ''}`,
        { timeout: 30000 },
      );
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      setItems([]); setTotal(0);
    } finally { setLoading(false); }
  }, [uf]);

  useEffect(() => { load(''); }, [load]);
  useEffect(() => {
    const id = setTimeout(() => load(q), 250);
    return () => clearTimeout(id);
  }, [q, load]);

  return (
    <div className="space-y-4" data-testid="bt-municipios">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar município (ex.: Recife)"
          className="pl-9 bg-neutral-900 border-neutral-800 text-white"
          data-testid="muni-search-input"
        />
      </div>
      <div className="text-xs text-neutral-500" data-testid="muni-total">
        {loading ? 'Carregando…' : `${num(total)} município(s)`}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((m) => (
          <Link
            key={m.id}
            to={`/brasil-transparente/municipio/${encodeURIComponent(m.nome)}`}
            className="px-3 py-2 rounded bg-neutral-900 border border-neutral-800 text-sm text-neutral-200 hover:border-emerald-500 hover:bg-neutral-900/70 transition flex items-center gap-2"
            data-testid={`muni-item-${m.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="truncate">{m.nome}</div>
              <div className="text-[10px] text-neutral-500 font-mono">IBGE {m.id}</div>
            </div>
            <ChevronRight size={14} className="text-neutral-500 shrink-0" />
          </Link>
        ))}
      </div>
      {!loading && items.length === 0 && (
        <EmptyLine msg="Nenhum município encontrado." />
      )}
    </div>
  );
}

/* ─── Emendas ─────────────────────────────────────────────── */
function EmendasTab({ uf }) {
  const [ano, setAno] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const load = useCallback(async (anoSel) => {
    setLoading(true); setErr(false);
    try {
      const url = `${API}/public/brasil-transparente/${uf}/emendas${anoSel ? `?ano=${anoSel}` : ''}`;
      const r = await axios.get(url, { timeout: 30000 });
      setData(r.data);
    } catch { setErr(true); }
    finally { setLoading(false); }
  }, [uf]);

  useEffect(() => { load(ano); }, [ano, load]);

  if (loading && !data) return <SkeletonBlock />;
  if (err) return <ErrorBox />;

  const porAno = data?.por_ano || [];
  const topMuni = data?.top_municipios || [];
  const topFav = data?.top_favorecidos || [];

  return (
    <div className="space-y-5" data-testid="bt-emendas">
      {/* Filtro ano */}
      <div className="flex gap-2 flex-wrap items-center">
        <button
          onClick={() => setAno('')}
          data-testid="ano-btn-all"
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
            ano === '' ? 'bg-emerald-500 text-neutral-950 border-emerald-500'
                       : 'bg-neutral-900 text-neutral-300 border-neutral-800'
          }`}
        >Todos anos</button>
        {porAno.map((y) => {
          const isEleitoral = y.ano === YEAR_ELEITORAL;
          return (
            <button
              key={y.ano}
              onClick={() => setAno(String(y.ano))}
              data-testid={`ano-btn-${y.ano}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition inline-flex items-center gap-1 ${
                String(ano) === String(y.ano)
                  ? 'bg-emerald-500 text-neutral-950 border-emerald-500'
                  : isEleitoral
                  ? 'bg-amber-500/10 text-amber-300 border-amber-500/50 hover:border-amber-400'
                  : 'bg-neutral-900 text-neutral-300 border-neutral-800'
              }`}
            >
              {isEleitoral && <Star size={11} />} {y.ano}
            </button>
          );
        })}
        {ano && String(ano) === String(YEAR_ELEITORAL) && (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/40 text-[10px] font-mono uppercase tracking-wider"
            data-testid="badge-eleitoral"
          >
            <Star size={11} /> Ano eleitoral
          </span>
        )}
      </div>

      {/* Resumo */}
      <Card className="bg-neutral-900 border-neutral-800 p-4" data-testid="emendas-resumo">
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-mono">
          Total {ano ? `em ${ano}` : 'acumulado'}
        </div>
        <div className="text-3xl sm:text-4xl font-black text-emerald-400 mt-1">
          {brl(
            ano
              ? (porAno.find((y) => String(y.ano) === String(ano))?.total || 0)
              : (data?.resumo?.total || 0)
          )}
        </div>
        <div className="text-xs text-neutral-500 mt-1">
          {num(
            ano
              ? (porAno.find((y) => String(y.ano) === String(ano))?.count || 0)
              : (data?.resumo?.count || 0)
          )} pagamentos
        </div>
      </Card>

      {/* Top municípios */}
      <Section title="Municípios que mais receberam" count={topMuni.length} testid="section-top-muni">
        <div className="space-y-1.5">
          {topMuni.slice(0, 10).map((m, i) => (
            <Link
              key={m.municipio + i}
              to={`/brasil-transparente/municipio/${encodeURIComponent(m.municipio)}`}
              className="block"
              data-testid={`top-muni-link-${i}`}
            >
              <RankRow
                rank={i + 1}
                label={m.municipio}
                value={brl(m.total)}
                hint={`${num(m.count)} pagamentos`}
                testid={`top-muni-${i}`}
                clickable
              />
            </Link>
          ))}
        </div>
      </Section>

      {/* Top favorecidos */}
      <Section title="Maiores beneficiários (CNPJ)" count={topFav.length} testid="section-top-fav">
        <div className="space-y-1.5">
          {topFav.slice(0, 10).map((f, i) => {
            const cnpjDigits = String(f.cnpj || '').replace(/\D/g, '');
            const hasCnpj = cnpjDigits.length === 14;
            const row = (
              <RankRow
                rank={i + 1}
                label={f.nome || 'Sem nome'}
                value={brl(f.total)}
                hint={hasCnpj ? `CNPJ ${f.cnpj}` : 'CNPJ não informado'}
                testid={`top-fav-${i}`}
                clickable={hasCnpj}
              />
            );
            return hasCnpj ? (
              <Link
                key={(f.cnpj || '') + i}
                to={`/brasil-transparente/empresa/${cnpjDigits}`}
                className="block"
                data-testid={`top-fav-link-${i}`}
              >
                {row}
              </Link>
            ) : (
              <div key={(f.cnpj || 'nocnpj') + i}>{row}</div>
            );
          })}
        </div>
      </Section>

      <div className="text-[11px] text-neutral-500 pt-2">
        Fonte: {data?.fonte || 'Portal da Transparência / Tesouro Transparente (CSV importado).'}
      </div>
    </div>
  );
}

/* ─── Deputados Estaduais (TSE 2022 eleitos) ─────────────── */
function EstaduaisTab({ uf }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErr(false);
      try {
        const r = await axios.get(
          `${API}/public/brasil-transparente/${uf}/deputados-estaduais`,
          { timeout: 30000 },
        );
        if (!cancel) setData(r.data);
      } catch { if (!cancel) setErr(true); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [uf]);

  if (loading) return <SkeletonBlock />;
  if (err || !data?.ok) return <ErrorBox />;

  const all = data.items || [];
  const filtered = q
    ? all.filter((d) =>
        (d.nome_urna + ' ' + d.nome_completo + ' ' + d.partido)
          .toLowerCase()
          .includes(q.toLowerCase()))
    : all;

  return (
    <div className="space-y-4" data-testid="bt-estaduais">
      <Card className="bg-neutral-900 border-neutral-800 p-4">
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-mono">
          Deputados estaduais eleitos · PE · mandato 2023–2027
        </div>
        <div className="text-3xl sm:text-4xl font-black text-emerald-400 mt-1">
          {num(all.length)}
        </div>
        <div className="text-xs text-neutral-500 mt-1">
          Fonte: TSE — Consulta Candidatos 2022 (dados abertos)
        </div>
      </Card>

      <Card className="bg-amber-900/20 border-amber-800/50 p-3 text-xs text-amber-200 flex gap-2">
        <Info size={14} className="shrink-0 mt-0.5" />
        <span>
          ALEPE não publica gastos de gabinete em API JSON aberta.
          Mostramos aqui os deputados <b>eleitos em 2022</b> com dados do TSE.
        </span>
      </Card>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nome ou partido"
          className="pl-9 bg-neutral-900 border-neutral-800 text-white"
          data-testid="estaduais-search"
        />
      </div>

      <div className="text-xs text-neutral-500" data-testid="estaduais-count">
        {num(filtered.length)} de {num(all.length)}
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        {filtered.map((d) => (
          <Card
            key={d.sq_candidato}
            className="bg-neutral-900 border-neutral-800 p-3"
            data-testid={`estadual-${d.sq_candidato}`}
          >
            <div className="text-sm font-semibold text-white truncate">{d.nome_urna}</div>
            {d.nome_completo && d.nome_completo !== d.nome_urna && (
              <div className="text-[10px] text-neutral-500 italic truncate">
                {d.nome_completo}
              </div>
            )}
            <div className="text-[11px] text-neutral-400 mt-0.5">
              <span className="text-emerald-400">{d.partido}</span>
              {d.escolaridade && <> · {d.escolaridade}</>}
            </div>
            {d.ocupacao && (
              <div className="text-[10px] text-neutral-500 mt-1">
                Ocupação declarada: {d.ocupacao}
              </div>
            )}
            {(d.data_nascimento || d.genero || d.cor_raca) && (
              <div className="text-[10px] text-neutral-500 mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                {d.data_nascimento && <span>Nasc.: {d.data_nascimento}</span>}
                {d.genero && <span>· {d.genero}</span>}
                {d.cor_raca && <span>· {d.cor_raca}</span>}
              </div>
            )}
            {d.coligacao_nome && (
              <div className="text-[10px] text-neutral-500 mt-1 truncate" title={d.coligacao_nome}>
                Coligação: {d.coligacao_nome}
              </div>
            )}
            <div className="text-[10px] text-neutral-500 font-mono mt-1">
              nº {d.nr_candidato} · {d.situacao}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}



/* ─── Gastos de gabinete (Câmara CEAP) ────────────────────── */
function GabineteTab({ uf }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErr(false);
      try {
        const r = await axios.get(
          `${API}/public/brasil-transparente/${uf}/deputados/ranking-gabinete`,
          { timeout: 90000 },
        );
        if (!cancel) setData(r.data);
      } catch { if (!cancel) setErr(true); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [uf]);

  if (loading) return (
    <div>
      <div className="text-xs text-neutral-500 mb-3">
        Consultando a Câmara — pode levar até 30s na primeira chamada…
      </div>
      <SkeletonBlock />
    </div>
  );
  if (err || !data?.ok) return <ErrorBox />;

  const items = data.items || [];
  return (
    <div className="space-y-4" data-testid="bt-gabinete">
      <Card className="bg-neutral-900 border-neutral-800 p-4">
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-mono">
          Ranking {data.ano} • {data.total_deputados} deputados PE
        </div>
        <div className="text-3xl sm:text-4xl font-black text-emerald-400 mt-1">
          {brl(data.total_geral)}
        </div>
        <div className="text-xs text-neutral-500 mt-1">
          Média por deputado: {brl(data.media)}
        </div>
      </Card>

      <div className="space-y-2">
        {items.map((d, i) => (
          <Link
            key={d.id}
            to={`/brasil-transparente/deputado/${d.id}`}
            className="block"
            data-testid={`gabinete-row-${d.id}`}
          >
            <Card className="bg-neutral-900 border-neutral-800 p-3 hover:border-emerald-500 transition flex items-center gap-3">
              <div className="text-xs font-mono text-neutral-500 w-5 shrink-0">
                {String(i + 1).padStart(2, '0')}
              </div>
              <img
                src={d.foto || 'https://via.placeholder.com/60x80?text=%20'}
                alt=""
                loading="lazy"
                className="w-10 h-12 object-cover rounded bg-neutral-800 shrink-0"
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white truncate">{d.nome}</div>
                <div className="text-[11px] text-neutral-400">
                  <span className="text-emerald-400">{d.partido}</span>
                  {d.top_categoria && (
                    <> · <span className="truncate">Top: {d.top_categoria}</span></>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-bold text-emerald-400 font-mono">{brl(d.total)}</div>
                <div className="text-[10px] text-neutral-500">{num(d.count)} desp.</div>
              </div>
              <ChevronRight size={14} className="text-neutral-500 shrink-0" />
            </Card>
          </Link>
        ))}
      </div>
      <div className="text-[11px] text-neutral-500">
        Fonte: {data.fonte}
      </div>
    </div>
  );
}

/* ─── Empresas beneficiadas ──────────────────────────────── */
function EmpresasTab({ uf }) {
  const [ano, setAno] = useState('');
  const [area, setArea] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const AREAS = [
    { k: '', label: 'Todas' },
    { k: 'saude', label: 'Saúde' },
    { k: 'educacao', label: 'Educação' },
    { k: 'infra', label: 'Infraestrutura' },
    { k: 'esporte', label: 'Esporte' },
    { k: 'assistencia', label: 'Assistência' },
    { k: 'cultura', label: 'Cultura' },
    { k: 'outros', label: 'Outros' },
  ];

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const q = [];
    if (ano) q.push(`ano=${ano}`);
    if (area) q.push(`area=${area}`);
    q.push('limit=50');
    axios.get(`${API}/public/brasil-transparente/${uf}/empresas?${q.join('&')}`, { timeout: 60000 })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData({ ok: false }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [uf, ano, area]);

  return (
    <div className="space-y-4" data-testid="bt-empresas">
      <div className="flex gap-2 flex-wrap">
        {AREAS.map((a) => (
          <button
            key={a.k}
            onClick={() => setArea(a.k)}
            data-testid={`area-btn-${a.k || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
              area === a.k
                ? 'bg-emerald-500 text-neutral-950 border-emerald-500'
                : 'bg-neutral-900 text-neutral-300 border-neutral-800'
            }`}
          >{a.label}</button>
        ))}
      </div>

      <Card className="bg-amber-900/20 border-amber-800/50 p-3 text-xs text-amber-200 flex gap-2">
        <AlertCircle size={14} className="shrink-0 mt-0.5" />
        <span>
          Classificação por área é <b>heurística automática</b> baseada no
          nome do favorecido. Não é oficial.
        </span>
      </Card>

      {loading && <SkeletonBlock />}
      {!loading && data?.ok && (
        <>
          <div className="text-xs text-neutral-500">
            {num(data.total_empresas)} beneficiários · {brl(data.total_valor)} consolidados
          </div>
          <div className="space-y-2">
            {(data.items || []).map((e, i) => (
              <Card
                key={(e.cnpj || e.nome) + i}
                className="bg-neutral-900 border-neutral-800 p-3"
                data-testid={`empresa-row-${i}`}
              >
                <div className="flex items-baseline gap-2 flex-wrap">
                  <div className="text-xs font-mono text-neutral-500 shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-white break-words">
                      {e.nome}
                    </div>
                    <div className="text-[10px] text-neutral-500 font-mono mt-0.5">
                      {e.cnpj || 'sem CNPJ'} · {e.area_label} · {e.municipios_count} municípios
                    </div>
                  </div>
                  <div className="text-sm font-bold text-emerald-400 font-mono shrink-0">
                    {brl(e.total)}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
      {!loading && data && !data.ok && <ErrorBox />}
    </div>
  );
}

/* ─── Salários ────────────────────────────────────────────── */
function SalariosTab({ uf }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/brasil-transparente/${uf}/salarios`, { timeout: 15000 });
        if (!cancel) setData(r.data);
      } catch { /* noop */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [uf]);

  if (loading) return <SkeletonBlock />;

  return (
    <div className="space-y-3" data-testid="bt-salarios">
      <Card className="bg-amber-900/20 border-amber-800/50 p-3 text-xs text-amber-200 flex gap-2">
        <AlertCircle size={14} className="shrink-0 mt-0.5" />
        <span>
          {data?.aviso ||
            'Salários-base oficiais. Não incluem verbas indenizatórias, cotas parlamentares ou auxílio-moradia.'}
        </span>
      </Card>

      <div className="space-y-2">
        {(data?.items || []).map((s, i) => (
          <Card
            key={s.cargo + i}
            className="bg-neutral-900 border-neutral-800 p-4"
            data-testid={`salario-item-${i}`}
          >
            <div className="flex items-baseline justify-between gap-4">
              <div className="text-sm font-semibold text-white">{s.cargo}</div>
              <div className="text-lg font-black text-emerald-400 font-mono shrink-0">
                {brl(s.subsidio_mensal)}
              </div>
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">
              {s.referencia}
            </div>
            <div className="text-[10px] text-neutral-600 font-mono mt-1">
              {s.fonte}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ─── Helpers UI ──────────────────────────────────────────── */
function MetricCard({ label, value, hint, testid }) {
  return (
    <Card className="bg-neutral-900 border-neutral-800 p-3 sm:p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">
        {label}
      </div>
      <div className="text-lg sm:text-2xl font-black text-white mt-1 leading-tight break-words">
        {value}
      </div>
      {hint && <div className="text-[10px] text-neutral-500 mt-1">{hint}</div>}
    </Card>
  );
}

function Section({ title, count, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-sm font-semibold text-neutral-200">{title}</h2>
        {typeof count === 'number' && (
          <Badge variant="outline" className="text-[10px] border-neutral-700 text-neutral-400">
            {num(count)}
          </Badge>
        )}
      </div>
      {children}
    </section>
  );
}

function RankRow({ rank, label, value, hint, testid, clickable }) {
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded bg-neutral-900 border border-neutral-800 ${clickable ? 'hover:border-emerald-500 transition' : ''}`}
      data-testid={testid}
    >
      <div className="text-xs font-mono text-neutral-500 w-5 shrink-0">
        {String(rank).padStart(2, '0')}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white truncate">{label}</div>
        {hint && <div className="text-[10px] text-neutral-500 font-mono truncate">{hint}</div>}
      </div>
      <div className="text-sm font-bold text-emerald-400 font-mono shrink-0">
        {value}
      </div>
      {clickable && <ChevronRight size={14} className="text-neutral-500 shrink-0" />}
    </div>
  );
}

function SkeletonBlock() {
  return (
    <div className="space-y-3 animate-pulse" data-testid="bt-skeleton">
      <div className="h-24 rounded bg-neutral-900 border border-neutral-800" />
      <div className="h-24 rounded bg-neutral-900 border border-neutral-800" />
      <div className="h-24 rounded bg-neutral-900 border border-neutral-800" />
    </div>
  );
}

function ErrorBox() {
  return (
    <Card className="bg-red-950/30 border-red-900 p-4 text-sm text-red-200 flex gap-2" data-testid="bt-error">
      <XCircle size={16} className="shrink-0 mt-0.5" />
      <div>
        Não foi possível carregar os dados agora. As fontes públicas podem estar
        temporariamente indisponíveis. Tente novamente em alguns instantes.
      </div>
    </Card>
  );
}

function EmptyLine({ msg }) {
  return (
    <div
      className="text-sm text-neutral-500 border border-dashed border-neutral-800 rounded px-3 py-4 text-center"
      data-testid="bt-empty"
    >
      {msg}
    </div>
  );
}

function FontesFooter({ uf }) {
  return (
    <div className="pt-3 border-t border-neutral-800/60 space-y-1 text-[11px] text-neutral-500">
      <div className="font-semibold text-neutral-400 mb-1 flex items-center gap-1.5">
        <ExternalLink size={12} /> Fontes oficiais ({uf}):
      </div>
      <div>• Câmara dos Deputados — Dados Abertos</div>
      <div>• Senado Federal — Dados Abertos</div>
      <div>• IBGE — Localidades</div>
      <div>• Tesouro Transparente / Portal da Transparência (emendas)</div>
    </div>
  );
}
